import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, Send, CheckCircle2, Loader2 } from "lucide-react";
import { api, wsUrl, getVisitorId } from "@/lib/api";
import { BrandMark, BrandLogo } from "@/components/ui/brand-logo";
import { ChatBubble } from "@/components/ui/chat-bubble";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function Contact() {
  const { id } = useParams();
  const visitorId = getVisitorId();
  const [contact, setContact] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [text, setText] = useState("");
  const [messages, setMessages] = useState([]);
  const [convId, setConvId] = useState(null);
  const [sending, setSending] = useState(false);
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const scannedRef = useRef(false);

  useEffect(() => {
    api.get(`/contact/${id}`).then((r) => setContact(r.data)).catch(() => setNotFound(true));
    if (!scannedRef.current) {
      scannedRef.current = true;
      api.post(`/contact/${id}/scan`).catch(() => {});
    }
    api.get(`/contact/${id}/thread`, { params: { visitor_id: visitorId } }).then((r) => {
      if (r.data.conversation) {
        setConvId(r.data.conversation.id);
        setMessages(r.data.messages);
      }
    }).catch(() => {});
  }, [id, visitorId]);

  useEffect(() => {
    if (!convId) return;
    const ws = new WebSocket(wsUrl(`conv:${convId}`));
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "message") {
        setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
      }
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [convId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/contact/${id}/message`, { visitor_id: visitorId, text: text.trim() });
      if (!convId) setConvId(r.data.conversation_id);
      setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
      setText("");
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-5">
        <div className="text-center">
          <BrandLogo className="justify-center" />
          <p className="mt-6 text-slate-500">This ReachMeNow code isn't valid or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-0 sm:p-6 font-body">
      <div className="w-full sm:max-w-md sm:rounded-[2.5rem] sm:border-[10px] sm:border-slate-900 sm:shadow-2xl overflow-hidden bg-white min-h-[100dvh] sm:min-h-0 sm:h-[85vh] flex flex-col" data-testid="contact-page">
        {/* Header */}
        <div className="bg-white px-6 pt-5 pb-4 sm:pt-8 sm:pb-5 border-b border-slate-100 flex flex-col items-center text-center shrink-0">
          <BrandMark className="!w-12 !h-12" />
          <p className="mt-2.5 text-xs font-semibold text-slate-400 flex items-center gap-1"><Lock className="h-3 w-3" /> Secure. Private. Simple.</p>
          {contact ? (
            <>
              <p className="mt-4 text-sm text-slate-500">You're reaching out privately to</p>
              <h1 className="font-heading font-bold text-2xl text-slate-900" data-testid="contact-owner-name">{contact.owner_name}</h1>
              <p className="mt-1 text-xs text-slate-400">Your phone number and identity are never shared.</p>
            </>
          ) : (
            <div className="mt-4 flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3 no-scrollbar bg-slate-50/50" data-testid="contact-messages">
          {messages.length === 0 && contact && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center">
                <Send className="h-6 w-6 text-brand-blue" />
              </div>
              <p className="mt-4 text-sm text-slate-500">Send your first message to {contact.owner_first_name}. They'll reply here in real time.</p>
            </div>
          )}
          {messages.map((m) => (
            <ChatBubble key={m.id} text={m.text} isOwner={m.sender === "owner"} time={fmt(m.timestamp)} />
          ))}
          {messages.length > 0 && messages[messages.length - 1].sender === "visitor" && (
            <div className="flex items-center gap-1.5 text-xs text-green-600 pl-1" data-testid="contact-delivered">
              <CheckCircle2 className="h-3.5 w-3.5" /> Delivered — {contact?.owner_first_name} will get back to you soon.
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <div className="border-t border-slate-100 p-3 bg-white shrink-0">
          <div className="flex items-end gap-2">
            <Textarea
              data-testid="contact-message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={contact ? `Type your message to reach ${contact.owner_first_name}…` : "Type your message…"}
              className="resize-none min-h-[44px] max-h-28 rounded-2xl border-slate-200 focus-visible:ring-brand-blue"
              rows={1}
            />
            <Button onClick={send} disabled={!text.trim() || sending} className="bg-brand-blue hover:bg-brand-bluehover rounded-full h-11 w-11 p-0 shrink-0" data-testid="contact-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
